/**
 * guidance-nudge-select — Logique PURE de sélection du prochain « coup de pouce ».
 *
 * Zéro dépendance React/DOM : importable en Vitest, testée en isolation
 * (cf. esprit `_shared/juliette-text-checks.ts`). Toute la décision « quel conseil
 * montrer maintenant, ou aucun » vit ici ; les effets (localStorage, timers, rendu)
 * sont ailleurs.
 *
 * Garanties produit câblées dans `selectNextNudge` :
 *  - conseils désactivés (interrupteur global) → rien ;
 *  - chaque conseil au plus UNE fois par session (`shownKeys`) → une astuce par
 *    nouvelle page visitée, mais pas de re-déclenchement en boucle ; l'effet
 *    « dégressif » émerge du « Ne plus voir » + du petit catalogue ;
 *  - une clé « Ne plus voir » (dismissed) ne revient jamais ;
 *  - une clé « Fermer » (snoozée) est reportée jusqu'à expiration ;
 *  - les conseils de ROUTE (contextuels) priment et ignorent le cooldown ;
 *  - les conseils AMBIANTS respectent un cooldown (anti-harcèlement inter-sessions).
 *
 * NB : « 1 bulle visible à la fois » est garanti en amont par le provider unique
 * (state partagé desktop/mobile), pas ici.
 */

export type NudgeScope = "route" | "ambient";

export interface NudgeCandidate {
  key: string;
  scope: NudgeScope;
  /** Base de route à matcher (requis pour scope "route"), ex. "/crm". */
  routeMatch?: string;
  /** Plus grand = préféré parmi les candidats éligibles. Défaut 0. */
  priority?: number;
}

export interface NudgeSelectionInput {
  /** Horodatage courant (ms). Injecté pour rester pur/testable. */
  now: number;
  pathname: string;
  /** Interrupteur global « afficher les conseils ». */
  nudgesEnabled: boolean;
  /** Clés déjà montrées dans la session courante (au plus 1 fois chacune). */
  shownKeys: readonly string[];
  /** Clés « Ne plus voir » (masquage définitif). */
  dismissedKeys: readonly string[];
  /** Clés « Fermer » → reportées jusqu'à cet horodatage (ms). */
  snoozedUntil: Readonly<Record<string, number>>;
  /** Dernier conseil affiché (ms) — pour le cooldown ambiant. null si jamais. */
  lastNudgeAt: number | null;
}

/** Délai minimal entre deux conseils AMBIANTS (6 h). Les conseils de route l'ignorent. */
export const AMBIENT_COOLDOWN_MS = 1000 * 60 * 60 * 6;

/**
 * Reprend la sémantique `isUnder` de juliette-page-context : route exacte ou
 * sous-route. Sûr pour le dashboard : `base="/"` ne matche QUE "/" (car le test
 * de sous-route compare à `"//"`, qu'aucun autre chemin ne préfixe).
 */
export function matchRoute(pathname: string, base: string): boolean {
  return pathname === base || pathname.startsWith(`${base}/`);
}

function byPriorityDesc(a: NudgeCandidate, b: NudgeCandidate): number {
  return (b.priority ?? 0) - (a.priority ?? 0);
}

/**
 * Renvoie la clé du conseil à afficher maintenant, ou null si aucun.
 * Les candidats sont fournis par l'appelant (catalogue `GUIDANCE_NUDGE_CANDIDATES`).
 */
export function selectNextNudge(
  candidates: readonly NudgeCandidate[],
  input: NudgeSelectionInput,
): string | null {
  if (!input.nudgesEnabled) return null;

  const isEligible = (c: NudgeCandidate): boolean => {
    if (input.shownKeys.includes(c.key)) return false;
    if (input.dismissedKeys.includes(c.key)) return false;
    const until = input.snoozedUntil[c.key];
    if (typeof until === "number" && until > input.now) return false;
    return true;
  };

  // 1) Conseils de ROUTE : contextuels, prioritaires, ignorent le cooldown.
  const routeMatch = candidates
    .filter(
      (c) =>
        c.scope === "route" &&
        !!c.routeMatch &&
        matchRoute(input.pathname, c.routeMatch) &&
        isEligible(c),
    )
    .sort(byPriorityDesc)[0];
  if (routeMatch) return routeMatch.key;

  // 2) Conseils AMBIANTS : soumis au cooldown.
  if (input.lastNudgeAt !== null && input.now - input.lastNudgeAt < AMBIENT_COOLDOWN_MS) {
    return null;
  }
  const ambient = candidates
    .filter((c) => c.scope === "ambient" && isEligible(c))
    .sort(byPriorityDesc)[0];
  return ambient?.key ?? null;
}
